import { coreApi } from '.';
import i18next from 'i18next';
import type {
  CreateModuleReq,
  CreateModuleRes,
  GetModuleByIdReq,
  GetModuleByIdRes,
  GetModulesReq,
  GetModulesRes,
  RemoveModuleReq,
  RemoveModuleRes,
  RemoveModulesReq,
  RemoveModulesRes,
  UpdateModuleReq,
  UpdateModuleRes,
  UpdateModulesOrderReq,
  UpdateModulesOrderRes,
  GetModulesWithAgendasRes,
  GetModulesWithAgendasReq,
  GetModulesDetailRes,
} from 'types';
import type { modulesDataScheme } from 'utils/table/admin/core/modules';
import { TAG_TYPES } from 'types/services';

type ModuleWithLocalizedName = {
  name: string;
} & Partial<{
  name_Cs: string;
  name_Sk: string;
  name_En: string;
}>;

const resolveModuleName = (module: ModuleWithLocalizedName): string => {
  const language = i18next.language.trim().toLowerCase();
  const localizedName = language.startsWith('en') ? module.name_En : language.startsWith('sk') ? module.name_Sk : module.name_Cs;

  const names = [localizedName, module.name, module.name_Cs, module.name_Sk, module.name_En];
  const resolvedName = names.find((name) => Boolean(name?.trim()));

  return resolvedName ?? '';
};

const normalizeModulesResponse = (response: GetModulesRes): GetModulesRes => {
  return {
    ...response,
    items: response.items.map((item) => ({
      ...item,
      name: resolveModuleName(item),
      childs: item.childs.map((child) => ({
        ...child,
        name: resolveModuleName(child),
      })),
    })),
  };
};

const normalizeModulesWithAgendasResponse = (response: GetModulesWithAgendasRes): GetModulesWithAgendasRes => {
  return {
    ...response,
    modules: response.modules.map((module) => ({
      ...module,
      name: resolveModuleName(module),
      childs: module.childs?.map((child) => ({
        ...child,
        name: resolveModuleName(child),
      })),
    })),
  };
};

const normalizeModulesDetailResponse = (response: GetModulesDetailRes): GetModulesDetailRes => {
  return {
    ...response,
    name: resolveModuleName(response),
    childs: response.childs.map((child) => ({
      ...child,
      name: resolveModuleName(child),
    })),
  };
};

export const modulesService = coreApi.injectEndpoints({
  endpoints: (builder) => ({
    createModule: builder.mutation<CreateModuleRes, CreateModuleReq>({
      query: (data) => ({
        url: 'Modules',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.MODULES],
    }),
    getModuleById: builder.query<GetModuleByIdRes, GetModuleByIdReq>({
      query: (data) => ({
        url: `Modules/${data.id}`,
        method: 'GET',
      }),
      providesTags: [TAG_TYPES.MODULES],
    }),
    removeModule: builder.mutation<RemoveModuleRes, RemoveModuleReq>({
      query: (data) => ({
        url: `Modules/${data.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [TAG_TYPES.MODULES],
    }),
    updateModule: builder.mutation<UpdateModuleRes, UpdateModuleReq>({
      query: (data) => ({
        url: `Modules/${data.id}`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: data.data,
      }),
      invalidatesTags: [TAG_TYPES.MODULES],
    }),
    getModules: builder.query<GetModulesRes, GetModulesReq<typeof modulesDataScheme>>({
      query: (data) => ({
        method: 'GET',
        url: 'Modules/All',
        params: {
          IsFlowVisible: data.isFlowVisible,
          IsHeliosVisible: data.isHeliosVisible,
          Page: data.page,
          PageSize: data.pageSize,
          OrderBy: data.orderBy,
          OrderDesc: data.orderDesc,
          Search: data.search,
          DataStructure: data.dataStructure,
        },
      }),
      transformResponse: normalizeModulesResponse,
      providesTags: [TAG_TYPES.MODULES],
    }),
    getModulesWithAgendas: builder.query<GetModulesWithAgendasRes, GetModulesWithAgendasReq>({
      query: (data) => ({
        method: 'GET',
        url: 'Modules/Agendas',
        params: {
          TakeAgendas: data.takeAgendas,
          TakeModules: data.takeModules,
          Search: data.search ?? '',
          DataStructure: data.dataStructure,
          OnlyPinnedModules: data.onlyPinnedModules,
          OnlyPinnedAgendas: data.onlyPinnedAgendas,
        },
      }),
      transformResponse: normalizeModulesWithAgendasResponse,
      providesTags: [TAG_TYPES.MODULES, TAG_TYPES.AGENDAS_FAVORITE],
    }),
    getModulesDetail: builder.query<GetModulesDetailRes, string>({
      query: (id) => ({
        url: `Modules/${id}/Detail`,
        method: 'GET',
      }),
      transformResponse: normalizeModulesDetailResponse,
      providesTags: [TAG_TYPES.MODULES, TAG_TYPES.AGENDAS_FAVORITE],
    }),
    removeModules: builder.mutation<RemoveModulesRes, RemoveModulesReq>({
      query: (data) => ({
        url: 'Modules',
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: data.ids,
      }),
      invalidatesTags: [TAG_TYPES.MODULES],
    }),
    updateModulesOrder: builder.mutation<UpdateModulesOrderRes, UpdateModulesOrderReq>({
      query: ({ params, body }) => ({
        url: 'Modules/Order',
        method: 'PUT',
        body,
        params,
      }),
      invalidatesTags: [TAG_TYPES.MODULES],
    }),
  }),
});
export const {
  useCreateModuleMutation,
  useGetModuleByIdQuery,
  useGetModulesQuery,
  useUpdateModuleMutation,
  useLazyGetModuleByIdQuery,
  useLazyGetModulesQuery,
  useRemoveModuleMutation,
  useRemoveModulesMutation,
  useUpdateModulesOrderMutation,
  useGetModulesWithAgendasQuery,
  useLazyGetModulesWithAgendasQuery,
  useGetModulesDetailQuery,
} = modulesService;
